import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { useState, useMemo, useEffect } from "react";
import { Plus, Trash2, AlertTriangle, CheckCircle2, Pencil, Flame, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/scorecard")({
  head: () => ({ meta: [{ title: "Scorecard — CEO Boardroom" }] }),
  component: ScorecardPage,
});

// ISO week-of Monday
function weekOfMonday(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

function ScorecardPage() {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-semibold">CEO Scorecard</h1>
        <p className="text-sm text-muted-foreground mt-1">3–5 numbers that tell you the truth. Blockers. Weekly reflection.</p>
      </div>
      <KpiSection userId={user.id} />
      <BlockerSection userId={user.id} />
      <ReflectionSection userId={user.id} />
    </div>
  );
}

/* ---------- KPIs ---------- */

type Cadence = "daily" | "weekly" | "monthly";
type Kpi = { id: string; label: string; unit: string | null; target: number | null; active: boolean; cadence: Cadence; goal_streak: number | null };
type Entry = { id: string; kpi_id: string; week_of: string; value: number };

function pad(n: number) { return n < 10 ? `0${n}` : `${n}`; }
function toISODate(d: Date) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }

function periodStart(cadence: Cadence, date = new Date()): string {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  if (cadence === "daily") return toISODate(d);
  if (cadence === "monthly") { d.setDate(1); return toISODate(d); }
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  d.setDate(d.getDate() + diff);
  return toISODate(d);
}

function prevPeriod(cadence: Cadence, iso: string): string {
  const [y, m, dd] = iso.split("-").map(Number);
  const d = new Date(y, m - 1, dd);
  if (cadence === "daily") d.setDate(d.getDate() - 1);
  else if (cadence === "weekly") d.setDate(d.getDate() - 7);
  else { d.setMonth(d.getMonth() - 1); d.setDate(1); }
  return toISODate(d);
}

function cadenceLabel(c: Cadence) { return c === "daily" ? "day" : c === "weekly" ? "week" : "month"; }
function cadenceLabelPlural(c: Cadence) { return c === "daily" ? "days" : c === "weekly" ? "weeks" : "months"; }

function computeStreak(kpi: Kpi, entries: Entry[]): number {
  if (!kpi.target) return 0;
  const map = new Map(entries.filter((e) => e.kpi_id === kpi.id).map((e) => [e.week_of, e.value]));
  let streak = 0;
  let cursor = periodStart(kpi.cadence);
  // Allow skipping the current period if empty, so a streak doesn't reset just because "today" isn't logged yet.
  if (!map.has(cursor)) cursor = prevPeriod(kpi.cadence, cursor);
  while (map.has(cursor) && (map.get(cursor) as number) >= kpi.target) {
    streak += 1;
    cursor = prevPeriod(kpi.cadence, cursor);
  }
  return streak;
}

function KpiSection({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState<Kpi | null>(null);

  const { data: kpis = [] } = useQuery({
    queryKey: ["kpis", userId],
    queryFn: async () => {
      const { data } = await supabase.from("kpis").select("*").eq("user_id", userId).eq("active", true).order("position");
      return (data ?? []) as Kpi[];
    },
  });

  const { data: entries = [] } = useQuery({
    queryKey: ["kpi_entries", userId],
    queryFn: async () => {
      const { data } = await supabase.from("kpi_entries").select("*").eq("user_id", userId).order("week_of", { ascending: false }).limit(400);
      return (data ?? []) as Entry[];
    },
  });

  const entryByKpiPeriod = useMemo(() => {
    const m = new Map<string, Entry>();
    entries.forEach((e) => m.set(`${e.kpi_id}:${e.week_of}`, e));
    return m;
  }, [entries]);

  const setValue = useMutation({
    mutationFn: async ({ kpi, value, period }: { kpi: Kpi; value: number; period: string }) => {
      const existing = entryByKpiPeriod.get(`${kpi.id}:${period}`);
      if (existing) {
        const { error } = await supabase.from("kpi_entries").update({ value }).eq("id", existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("kpi_entries").insert({ user_id: userId, kpi_id: kpi.id, week_of: period, value });
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["kpi_entries"] }),
  });

  const checkOff = useMutation({
    mutationFn: async ({ kpi, period, hit }: { kpi: Kpi; period: string; hit: boolean }) => {
      const value = hit ? (kpi.target ?? 1) : 0;
      const existing = entryByKpiPeriod.get(`${kpi.id}:${period}`);
      if (existing) {
        const { error } = await supabase.from("kpi_entries").update({ value }).eq("id", existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("kpi_entries").insert({ user_id: userId, kpi_id: kpi.id, week_of: period, value });
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["kpi_entries"] }),
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("kpis").update({ active: false }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["kpis"] }),
  });

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground">KPIs & Streaks</h2>
        {kpis.length < 8 && (
          <Button size="sm" variant="ghost" onClick={() => setDlg(true)}><Plus className="h-3.5 w-3.5 mr-1" /> Add KPI</Button>
        )}
      </div>

      {kpis.length === 0 ? (
        <div className="boardroom-card p-8 text-center">
          <p className="text-foreground/80 mb-1">No KPIs yet.</p>
          <p className="text-sm text-muted-foreground">Track numbers ("$10k/month for 3 months in a row") or reps ("15 outreaches a day").</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {kpis.map((k) => {
            const period = periodStart(k.cadence);
            const current = entryByKpiPeriod.get(`${k.id}:${period}`);
            const streak = computeStreak(k, entries);
            const goalMet = k.goal_streak != null && streak >= k.goal_streak;
            const hitThisPeriod = k.target != null && (current?.value ?? 0) >= k.target;

            // Build last 8 periods (oldest → newest) so streak dots read left→right.
            const periods: string[] = [];
            let cursor = period;
            for (let i = 0; i < 8; i++) { periods.push(cursor); cursor = prevPeriod(k.cadence, cursor); }
            periods.reverse();

            const pct = k.target ? Math.min(100, ((current?.value ?? 0) / k.target) * 100) : 0;

            return (
              <div key={k.id} className="boardroom-card p-4 group">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground truncate">{k.label}</div>
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground/70 mt-0.5">
                      {k.target ? `${k.target}${k.unit ? ` ${k.unit}` : ""} / ${cadenceLabel(k.cadence)}` : `per ${cadenceLabel(k.cadence)}`}
                      {k.goal_streak ? ` · goal ${k.goal_streak} ${cadenceLabelPlural(k.cadence)}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 shrink-0">
                    <button onClick={() => setEditing(k)} className="text-muted-foreground hover:text-[color:var(--gold)]" aria-label="Edit KPI">
                      <Pencil className="h-3 w-3" />
                    </button>
                    <button onClick={() => del.mutate(k.id)} className="text-muted-foreground hover:text-[color:var(--danger)]" aria-label="Remove KPI">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-3 mt-3">
                  <Input
                    key={current?.id ?? `${k.id}-empty`}
                    type="number"
                    defaultValue={current?.value ?? ""}
                    onBlur={(e) => {
                      const v = parseFloat(e.target.value);
                      if (!isNaN(v)) setValue.mutate({ kpi: k, value: v, period });
                    }}
                    placeholder="0"
                    className="text-2xl font-display font-semibold tabular text-[color:var(--gold)] w-24 h-10 px-2 bg-transparent border-[color:var(--hairline)]"
                  />
                  <button
                    type="button"
                    onClick={() => checkOff.mutate({ kpi: k, period, hit: !hitThisPeriod })}
                    className={`h-10 w-10 rounded-md border flex items-center justify-center transition ${hitThisPeriod ? "bg-[color:var(--teal)]/20 border-[color:var(--teal)] text-[color:var(--teal)]" : "border-[color:var(--hairline)] text-muted-foreground hover:text-foreground"}`}
                    aria-label={hitThisPeriod ? "Mark not hit" : "Mark hit"}
                    title={k.target ? `Instantly mark this ${cadenceLabel(k.cadence)} as ${k.target}${k.unit ? " " + k.unit : ""}` : "Mark this period done"}
                  >
                    <Check className="h-4 w-4" />
                  </button>

                  <div className="ml-auto flex items-center gap-1 text-xs">
                    <Flame className={`h-3.5 w-3.5 ${streak > 0 ? "text-[color:var(--gold)]" : "text-muted-foreground"}`} />
                    <span className={`tabular font-semibold ${goalMet ? "text-[color:var(--gold)]" : "text-foreground"}`}>{streak}</span>
                    {k.goal_streak ? <span className="text-muted-foreground">/ {k.goal_streak}</span> : null}
                  </div>
                </div>

                {k.target && (
                  <div className="w-full h-1 bg-[color:var(--hairline)] rounded-full mt-3 overflow-hidden">
                    <div className="h-full bg-[color:var(--gold)]" style={{ width: `${pct}%` }} />
                  </div>
                )}

                <div className="flex items-center gap-1 mt-3">
                  {periods.map((p) => {
                    const e = entryByKpiPeriod.get(`${k.id}:${p}`);
                    const hit = k.target != null && (e?.value ?? 0) >= k.target;
                    const isNow = p === period;
                    return (
                      <div
                        key={p}
                        title={`${p}: ${e?.value ?? 0}${k.target ? ` / ${k.target}` : ""}`}
                        className={`flex-1 h-2 rounded-sm ${hit ? "bg-[color:var(--teal)]" : e ? "bg-[color:var(--hairline)]" : "bg-[color:var(--hairline)]/40"} ${isNow ? "ring-1 ring-[color:var(--gold)]/60" : ""}`}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <KpiDialog open={dlg} onOpenChange={setDlg} userId={userId} count={kpis.length} />
      <KpiDialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)} userId={userId} count={kpis.length} editing={editing} />
    </section>
  );
}

function KpiDialog({ open, onOpenChange, userId, count, editing }: { open: boolean; onOpenChange: (v: boolean) => void; userId: string; count: number; editing?: Kpi | null }) {
  const qc = useQueryClient();
  const [label, setLabel] = useState("");
  const [unit, setUnit] = useState("");
  const [target, setTarget] = useState("");
  const [cadence, setCadence] = useState<Cadence>("weekly");
  const [goalStreak, setGoalStreak] = useState("");

  useMemo(() => {
    if (editing) {
      setLabel(editing.label);
      setUnit(editing.unit ?? "");
      setTarget(editing.target != null ? String(editing.target) : "");
      setCadence(editing.cadence);
      setGoalStreak(editing.goal_streak != null ? String(editing.goal_streak) : "");
    } else if (open) {
      setLabel(""); setUnit(""); setTarget(""); setCadence("weekly"); setGoalStreak("");
    }
  }, [editing?.id, open]);

  async function submit() {
    if (!label.trim()) return;
    const payload = {
      label: label.trim(),
      unit: unit.trim() || null,
      target: target ? parseFloat(target) : null,
      cadence,
      goal_streak: goalStreak ? parseInt(goalStreak, 10) : null,
    };
    if (editing) {
      const { error } = await supabase.from("kpis").update(payload).eq("id", editing.id);
      if (error) return toast.error(error.message);
      toast.success("KPI updated");
    } else {
      const { error } = await supabase.from("kpis").insert({ ...payload, user_id: userId, position: count });
      if (error) return toast.error(error.message);
      toast.success("KPI added");
    }
    qc.invalidateQueries({ queryKey: ["kpis"] });
    onOpenChange(false);
  }

  const cadenceOptions: { value: Cadence; label: string }[] = [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[color:var(--surface-1)] border-[color:var(--hairline)] max-w-sm">
        <DialogHeader><DialogTitle>{editing ? "Edit KPI" : "New KPI"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs mb-1.5 block">Name</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Monthly revenue, Daily outreach" autoFocus />
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">Cadence</Label>
            <div className="grid grid-cols-3 gap-1.5">
              {cadenceOptions.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => setCadence(o.value)}
                  className={`h-8 rounded-md text-xs border transition ${cadence === o.value ? "bg-[color:var(--gold)]/15 border-[color:var(--gold)] text-[color:var(--gold)]" : "border-[color:var(--hairline)] text-muted-foreground hover:text-foreground"}`}
                >{o.label}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs mb-1.5 block">Target per {cadenceLabel(cadence)}</Label>
              <Input type="number" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="10000" />
            </div>
            <div>
              <Label className="text-xs mb-1.5 block">Unit</Label>
              <Input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="$, opens, calls" />
            </div>
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">Streak goal ({cadenceLabelPlural(cadence)} in a row, optional)</Label>
            <Input type="number" value={goalStreak} onChange={(e) => setGoalStreak(e.target.value)} placeholder="3" />
          </div>
          <Button onClick={submit} className="w-full bg-[color:var(--gold)] text-[color:var(--primary-foreground)]">{editing ? "Save changes" : "Add KPI"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/* ---------- Blockers ---------- */

type Blocker = { id: string; title: string; description: string | null; status: string; resolved_at: string | null; created_at: string };

function BlockerSection({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState<Blocker | null>(null);

  const { data: blockers = [] } = useQuery({
    queryKey: ["blockers", userId],
    queryFn: async () => {
      const { data } = await supabase.from("blockers").select("*").eq("user_id", userId).order("status").order("created_at", { ascending: false });
      return (data ?? []) as Blocker[];
    },
  });

  const toggle = useMutation({
    mutationFn: async (b: Blocker) => {
      const resolved = b.status !== "resolved";
      const { error } = await supabase.from("blockers").update({ status: resolved ? "resolved" : "open", resolved_at: resolved ? new Date().toISOString() : null }).eq("id", b.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["blockers"] }),
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("blockers").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["blockers"] }),
  });

  const open = blockers.filter((b) => b.status !== "resolved");
  const resolved = blockers.filter((b) => b.status === "resolved");

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5 text-[color:var(--danger)]" /> Current Blockers
          {open.length > 0 && <span className="text-[color:var(--danger)] tabular">({open.length})</span>}
        </h2>
        <Button size="sm" variant="ghost" onClick={() => { setEditing(null); setDlg(true); }}><Plus className="h-3.5 w-3.5 mr-1" /> Add Blocker</Button>
      </div>

      {blockers.length === 0 ? (
        <div className="boardroom-card p-6 text-center">
          <p className="text-sm text-muted-foreground">No blockers. Name what's stuck and get it moving.</p>
        </div>
      ) : (
        <div className="boardroom-card divide-y divide-[color:var(--hairline)]">
          {[...open, ...resolved].map((b) => (
            <div key={b.id} className="p-4 flex items-start gap-3 group">
              <button onClick={() => toggle.mutate(b)} className="mt-0.5">
                {b.status === "resolved" ? (
                  <CheckCircle2 className="h-4 w-4 text-[color:var(--teal)]" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-[color:var(--danger)]" />
                )}
              </button>
              <div className="flex-1 min-w-0">
                <div className={`text-sm ${b.status === "resolved" ? "line-through text-muted-foreground" : "text-foreground"}`}>{b.title}</div>
                {b.description && <div className="text-xs text-muted-foreground mt-0.5">{b.description}</div>}
              </div>
              <button onClick={() => { setEditing(b); setDlg(true); }} className="opacity-100 md:opacity-0 md:group-hover:opacity-100 text-muted-foreground hover:text-foreground" aria-label="Edit">
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => { if (confirm("Delete this blocker?")) del.mutate(b.id); }} className="opacity-100 md:opacity-0 md:group-hover:opacity-100 text-muted-foreground hover:text-[color:var(--danger)]">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <BlockerDialog open={dlg} onOpenChange={(v) => { setDlg(v); if (!v) setEditing(null); }} userId={userId} editing={editing} />
    </section>
  );
}

function BlockerDialog({ open, onOpenChange, userId, editing }: { open: boolean; onOpenChange: (v: boolean) => void; userId: string; editing?: Blocker | null }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (open) {
      setTitle(editing?.title ?? "");
      setDescription(editing?.description ?? "");
    }
  }, [open, editing?.id]);

  async function save() {
    if (!title.trim()) return;
    const payload = { title: title.trim(), description: description.trim() || null };
    if (editing) {
      const { error } = await supabase.from("blockers").update(payload).eq("id", editing.id);
      if (error) return toast.error(error.message);
      toast.success("Blocker updated");
    } else {
      const { error } = await supabase.from("blockers").insert({ user_id: userId, ...payload });
      if (error) return toast.error(error.message);
      toast.success("Blocker logged");
    }
    qc.invalidateQueries({ queryKey: ["blockers"] });
    onOpenChange(false);
    setTitle(""); setDescription("");
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[color:var(--surface-1)] border-[color:var(--hairline)] max-w-md">
        <DialogHeader><DialogTitle>{editing ? "Edit Blocker" : "New Blocker"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs mb-1.5 block">What's stuck?</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus /></div>
          <div><Label className="text-xs mb-1.5 block">Details (optional)</Label><Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} /></div>
          <Button onClick={save} className="w-full bg-[color:var(--gold)] text-[color:var(--primary-foreground)]">{editing ? "Save changes" : "Log Blocker"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Reflection ---------- */

type Reflection = { id: string; week_of: string; went_well: string | null; didnt_work: string | null; next_week_focus: string | null };

function ReflectionSection({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const week = weekOfMonday();

  const { data: current } = useQuery({
    queryKey: ["reflection-current", userId, week],
    queryFn: async () => {
      const { data } = await supabase.from("reflections").select("*").eq("user_id", userId).eq("week_of", week).maybeSingle();
      return data as Reflection | null;
    },
  });

  const { data: past = [] } = useQuery({
    queryKey: ["reflections", userId],
    queryFn: async () => {
      const { data } = await supabase.from("reflections").select("*").eq("user_id", userId).neq("week_of", week).order("week_of", { ascending: false }).limit(6);
      return (data ?? []) as Reflection[];
    },
  });

  const [wentWell, setWentWell] = useState("");
  const [didntWork, setDidntWork] = useState("");
  const [nextFocus, setNextFocus] = useState("");

  useMemo(() => {
    setWentWell(current?.went_well ?? "");
    setDidntWork(current?.didnt_work ?? "");
    setNextFocus(current?.next_week_focus ?? "");
  }, [current?.id]);

  async function save() {
    const payload = {
      user_id: userId,
      week_of: week,
      went_well: wentWell.trim() || null,
      didnt_work: didntWork.trim() || null,
      next_week_focus: nextFocus.trim() || null,
    };
    const { error } = current
      ? await supabase.from("reflections").update(payload).eq("id", current.id)
      : await supabase.from("reflections").insert(payload);
    if (error) return toast.error(error.message);
    qc.invalidateQueries({ queryKey: ["reflection-current"] });
    qc.invalidateQueries({ queryKey: ["reflections"] });
    toast.success("Reflection saved");
  }

  return (
    <section className="space-y-4">
      <h2 className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground">Weekly Reflection · Week of {new Date(week + "T00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}</h2>
      <div className="boardroom-card p-5 space-y-4">
        <div><Label className="text-xs mb-1.5 block text-[color:var(--teal)]">What worked?</Label>
          <Textarea value={wentWell} onChange={(e) => setWentWell(e.target.value)} rows={2} placeholder="Wins, breakthroughs, momentum you want to protect." />
        </div>
        <div><Label className="text-xs mb-1.5 block text-[color:var(--danger)]">What didn't?</Label>
          <Textarea value={didntWork} onChange={(e) => setDidntWork(e.target.value)} rows={2} placeholder="Where did time and energy leak?" />
        </div>
        <div><Label className="text-xs mb-1.5 block text-[color:var(--gold)]">Next week's focus</Label>
          <Textarea value={nextFocus} onChange={(e) => setNextFocus(e.target.value)} rows={2} placeholder="The single thing that would make next week a win." />
        </div>
        <Button onClick={save} className="bg-[color:var(--gold)] text-[color:var(--primary-foreground)]">Save Reflection</Button>
      </div>

      {past.length > 0 && (
        <details className="boardroom-card">
          <summary className="p-4 cursor-pointer text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground">Past reflections ({past.length})</summary>
          <div className="divide-y divide-[color:var(--hairline)]">
            {past.map((r) => (
              <div key={r.id} className="p-4 space-y-1.5 text-sm">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground tabular">Week of {new Date(r.week_of + "T00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</div>
                {r.went_well && <div><span className="text-[color:var(--teal)] text-xs">Worked:</span> {r.went_well}</div>}
                {r.didnt_work && <div><span className="text-[color:var(--danger)] text-xs">Didn't:</span> {r.didnt_work}</div>}
                {r.next_week_focus && <div><span className="text-[color:var(--gold)] text-xs">Focus:</span> {r.next_week_focus}</div>}
              </div>
            ))}
          </div>
        </details>
      )}
    </section>
  );
}
